import React, { useState, useEffect, useMemo } from "react";
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  TableToolbar,
  TableToolbarContent,
  TableToolbarSearch,
  Pagination,
  IconButton,
  DataTableSkeleton,
} from "@carbon/react";
import { Download } from "@carbon/icons-react";
import { useTranslation } from "react-i18next";
import { Workbook } from "exceljs";
import saveAs from "file-saver";
import { useGetFuelRequestsHistory } from "../../../hooks/useGetFuelRequestsHistory";
import { fuelRequestsHistoryMapper } from "../../../mappers/fuelRequestsHistoryMapper";
import { useFuelRequestsStore } from "../../../store/fuelRequestStore";
import { useGetTableHistory } from "../../../hooks/useGetTableHistory";
import { useOutletContext, useNavigate } from "react-router-dom";

export default function FuelHistory() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [fuelHistory, setFuelHistory] = useState([]);

  const { uplinkAgent, nick } = useOutletContext();

  const { fuelHistoryRequestsFilters, setFuelHistoryRequestsFilters } =
    useFuelRequestsStore();

  const { search, pagination, sort } = fuelHistoryRequestsFilters;

  const formatDate = (date) => date.toISOString().split("T")[0];

  const today = new Date();
  const monthAgo = new Date();
  monthAgo.setMonth(today.getMonth() - 1);

  const {
    data: requestsHistory,
    isPending: isHistoryPending,
    isSuccess: isHistorySuccess,
    error,
  } = useGetFuelRequestsHistory({
    startDate: formatDate(monthAgo),
    endDate: formatDate(today),
    status: ["APPROVED", "CANCELED", "CANCELEDBYTIMEOUT"],
  });

  useEffect(() => {
    if (error) {
      navigate("/error", {
        state: {
          code: error.code,
          message: error.message,
        },
      });
    }
  }, [error]);

  useEffect(() => {
    if (isHistorySuccess) {
      setFuelHistory(requestsHistory);
    }
  }, [isHistorySuccess, requestsHistory]);

  const mutationHistoryTable = useGetTableHistory();

  const { mappedHeaders, mappedRows } = fuelRequestsHistoryMapper(
    fuelHistory,
    uplinkAgent,
    t
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return mappedRows;

    return mappedRows.filter((row) =>
      Object.values(row).some((value) =>
        String(value).toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [mappedRows, search]);

  const sortedRows = useMemo(() => {
    if (!sort.key || sort.direction === "NONE") return filteredRows;

    const parseValue = (val) => {
      if (val == null) return "";

      const trimmed = val.trim();

      // Проверка: это число?
      const number = Number(trimmed);
      if (!isNaN(number) && trimmed !== "") {
        return { type: "number", value: number };
      }

      // Проверка: это дата?
      const date = Date.parse(trimmed);
      if (!isNaN(date)) {
        return { type: "date", value: date };
      }

      // Иначе — это строка
      return { type: "string", value: trimmed.toLowerCase() };
    };

    const compare = (aRaw, bRaw) => {
      const a = parseValue(aRaw);
      const b = parseValue(bRaw);

      if (a.type === b.type) {
        if (a.value === b.value) return 0;
        return a.value > b.value ? 1 : -1;
      }

      // Типы разные: приоритет — number > date > string
      const priority = { number: 1, date: 2, string: 3 };
      return priority[a.type] - priority[b.type];
    };

    const sorted = [...filteredRows].sort((a, b) => {
      const aVal = a[sort.key];
      const bVal = b[sort.key];

      const result = compare(aVal, bVal);
      return sort.direction === "ASC" ? result : -result;
    });

    return sorted;
  }, [filteredRows, sort]);

  const paginatedRows = useMemo(() => {
    const start = (pagination.page - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return sortedRows.slice(start, end);
  }, [sortedRows, pagination]);

  const handleExportToExcel = async () => {
    try {
      const response = await mutationHistoryTable.mutateAsync({
        status: ["APPROVED", "CANCELED", "CANCELEDBYTIMEOUT"],
      });

      const { mappedHeaders: exportColumns, mappedRows: exportRows } =
        fuelRequestsHistoryMapper(
          response.data?.data?.fuelRequests,
          uplinkAgent,
          t
        );

      if (!Array.isArray(exportRows) || exportRows.length === 0) {
        return;
      }

      const workbook = new Workbook();
      const worksheet = workbook.addWorksheet("Main sheet");

      const columns = exportColumns.map(({ header, key }) => ({
        header: t(header),
        key,
        width: 20,
      }));

      worksheet.columns = columns;

      exportRows.forEach((item) => {
        worksheet.addRow(item);
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      saveAs(blob, "Requests_history.xlsx");
    } catch (error) {
      navigate("/error", {
        state: {
          message: error.code,
          action: error.message,
        },
      });
    }
  };

  return (
    <div className="main">
      {isHistoryPending ? (
        <DataTableSkeleton
          aria-label="sample table"
          headers={[
            { key: "code", header: t("main.fuel.fuelHistoryTable.code") },
            { key: "client", header: t("main.fuel.fuelHistoryTable.client") },
            { key: "fuel", header: t("main.fuel.fuelHistoryTable.fuel") },
            { key: "created", header: t("main.fuel.fuelHistoryTable.created") },
            {
              key: "resolutionAt",
              header: t("main.fuel.fuelHistoryTable.closed"),
            },
            { key: "state", header: t("main.fuel.fuelHistoryTable.state") },
          ]}
          showHeader
          showToolbar
        />
      ) : (
        <DataTable
          size="lg"
          headers={mappedHeaders}
          rows={paginatedRows}
          isSortable
          useZebraStyles
          render={({ rows, headers, getHeaderProps }) => (
            <TableContainer
              title={`${t("main.fuel.fuelHistoryTable.title", { name: nick })}`}
              description={`${
                mappedRows.length
                  ? `${t("main.fuel.fuelHistoryTable.desc")}`
                  : `${t("main.fuel.fuelHistoryTable.empty")}`
              }`}
            >
              <TableToolbar>
                <TableToolbarContent className="main_fh_toolbar">
                  <IconButton
                    label={`${t("main.fuel.fuelHistoryTable.download")}`}
                    kind="ghost"
                    size="lg"
                    onClick={handleExportToExcel}
                    autoAlign
                  >
                    <Download />
                  </IconButton>
                  <TableToolbarSearch
                    value={search}
                    defaultExpanded={!!search}
                    persistent={!!search}
                    placeholder={`${t("main.tableText.placeholder")}`}
                    closeButtonLabelText={`${t(
                      "main.searchText.closeButtonText"
                    )}`}
                    onChange={(e) => {
                      setFuelHistoryRequestsFilters({
                        ...fuelHistoryRequestsFilters,
                        search: e.target.value,
                        pagination: {
                          ...fuelHistoryRequestsFilters.pagination,
                          page: 1,
                        },
                      });
                    }}
                  />
                </TableToolbarContent>
              </TableToolbar>
              <Table useZebraStyles>
                <TableHead>
                  <TableRow>
                    {headers.map((header) => {
                      const sort = fuelHistoryRequestsFilters.sort;
                      const isSorted = sort.key === header.key;

                      const { key, ...headerProps } = getHeaderProps({
                        header,
                        onClick: () => {
                          let nextDirection;

                          if (sort.key !== header.key) {
                            nextDirection = "ASC";
                          } else if (sort.direction === "ASC") {
                            nextDirection = "DESC";
                          } else if (sort.direction === "DESC") {
                            nextDirection = "NONE";
                          } else {
                            nextDirection = "ASC";
                          }

                          setFuelHistoryRequestsFilters({
                            sort: {
                              key: header.key,
                              direction: nextDirection,
                            },
                          });
                        },
                      });

                      return (
                        <TableHeader
                          key={key}
                          {...headerProps}
                          isSortHeader={isSorted && sort.direction !== "NONE"}
                          sortDirection={isSorted ? sort.direction : "NONE"}
                        >
                          {t(header.header)}
                        </TableHeader>
                      );
                    })}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      {row.cells.map((cell) => (
                        <TableCell key={cell.id}>{cell.value}</TableCell>
                      ))}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {paginatedRows.length ? (
                <Pagination
                  backwardText={`${t(
                    "main.tableText.pagination.backwardText"
                  )}`}
                  forwardText={`${t("main.tableText.pagination.forwardText")}`}
                  itemsPerPageText={`${t(
                    "main.tableText.pagination.itemsPerPageText"
                  )}`}
                  page={pagination.page}
                  onChange={({ page, pageSize }) => {
                    setFuelHistoryRequestsFilters({
                      ...fuelHistoryRequestsFilters,
                      pagination: { page, pageSize },
                    });
                  }}
                  pageNumberText={`${t(
                    "main.tableText.pagination.pageNumberText"
                  )}`}
                  itemRangeText={(min, max, total) =>
                    `${min}–${max} ${t(
                      "main.tableText.pagination.range"
                    )} ${total} ${
                      total === 1
                        ? t("main.tableText.pagination.itemRangeText.item")
                        : t("main.tableText.pagination.itemRangeText.items")
                    }`
                  }
                  pageRangeText={(current, total) =>
                    `${t("main.tableText.pagination.range")} ${total} ${
                      total === 1
                        ? t("main.tableText.pagination.pageRangeText.page")
                        : t("main.tableText.pagination.pageRangeText.pages")
                    }`
                  }
                  pageSize={pagination.pageSize}
                  pageSizes={[10, 15, 20]}
                  size="md"
                  totalItems={filteredRows.length}
                />
              ) : null}
            </TableContainer>
          )}
        />
      )}
    </div>
  );
}
